// Система отслеживания времени просмотра для ScreenifyMSX
export class WatchTimeTracker {
  private startTime: number = 0;
  private pausedTime: number = 0;
  private isPaused: boolean = true;
  private intervalId: number | null = null;
  private totalWatchTime: number = 0;
  private sessionKey: string;

  constructor(sessionKey: string) {
    this.sessionKey = sessionKey;
    this.loadFromSession();
  }

  // Загружаем сохраненное время из sessionStorage
  private loadFromSession(): void {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(this.sessionKey);
      if (stored) {
        this.totalWatchTime = parseInt(stored, 10) || 0;
      }
    }
  }

  // Сохраняем время в sessionStorage
  private saveToSession(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(this.sessionKey, this.totalWatchTime.toString());
    }
  }

  // Начинаем отслеживание времени
  public start(): void {
    if (!this.isPaused) return;
    
    this.isPaused = false;
    this.startTime = Date.now();
    
    // Обновляем каждую секунду
    this.intervalId = window.setInterval(() => {
      if (!this.isPaused) {
        this.totalWatchTime += 1;
        this.saveToSession();
      }
    }, 1000);
  }

  // Паузируем отслеживание
  public pause(): void {
    if (this.isPaused) return;
    
    this.isPaused = true;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Получаем общее время просмотра в секундах
  public getWatchTime(): number {
    return this.totalWatchTime;
  }

  // Получаем время просмотра в минутах (с 1 знаком после запятой)
  public getWatchTimeInMinutes(): string {
    return (this.totalWatchTime / 60).toFixed(1);
  }

  // Сбрасываем трекер
  public reset(): void {
    this.pause();
    this.totalWatchTime = 0;
    this.startTime = 0;
    this.pausedTime = 0;
    this.saveToSession();
  }

  // Очищаем данные при завершении
  public cleanup(): void {
    this.pause();
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(this.sessionKey);
    }
  }

  // Проверяем, активно ли отслеживание
  public isTracking(): boolean {
    return !this.isPaused;
  }
}

// Глобальный трекер для текущего контента
let globalWatchTimeTracker: WatchTimeTracker | null = null;

// Функция для создания трекера для конкретного контента
export function createWatchTimeTracker(contentId: string): WatchTimeTracker {
  const sessionKey = `screenify:watchTime:${contentId}`;
  return new WatchTimeTracker(sessionKey);
}

// Функция для получения глобального трекера
export function getGlobalWatchTimeTracker(): WatchTimeTracker | null {
  return globalWatchTimeTracker;
}

// Функция для установки глобального трекера
export function setGlobalWatchTimeTracker(tracker: WatchTimeTracker | null): void {
  globalWatchTimeTracker = tracker;
}

// Функция для очистки глобального трекера
export function cleanupGlobalWatchTimeTracker(): void {
  if (globalWatchTimeTracker) {
    globalWatchTimeTracker.cleanup();
    globalWatchTimeTracker = null;
  }
}

// Обработчик для события beforeunload (отправка аналитики при закрытии страницы)
export function setupBeforeUnloadHandler(sendAnalyticsCallback: () => void): void {
  if (typeof window !== 'undefined') {
    const handleBeforeUnload = () => {
      if (globalWatchTimeTracker && globalWatchTimeTracker.getWatchTime() > 0) {
        // Используем sendBeacon для надежной отправки при закрытии страницы
        sendAnalyticsCallback();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Возвращаем функцию для удаления обработчика
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }
}
